import clsx from "clsx";
import { useId, useState, type KeyboardEvent, type ReactNode } from "react";

export interface TabItem {
  id: string;
  label: ReactNode;
  panel: ReactNode;
  disabled?: boolean;
  /** A small number beside the label (how many are waiting). */
  count?: number;
}

interface TabsProps {
  tabs: readonly TabItem[];
  /** The selected tab, when the parent keeps it. */
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  /** Names the set of tabs for screen readers. */
  label: string;
  className?: string;
}

/** Tabs that work from the keyboard: arrow keys move, Home and End jump. Only the open panel is drawn. */
export function Tabs({ tabs, value, defaultValue, onChange, label, className }: TabsProps) {
  const base = useId();
  const [inner, setInner] = useState(defaultValue ?? tabs.find((t) => !t.disabled)?.id ?? "");
  const selected = value ?? inner;
  const enabled = tabs.filter((t) => !t.disabled);

  const select = (id: string) => {
    if (value === undefined) setInner(id);
    onChange?.(id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, id: string) => {
    const index = enabled.findIndex((t) => t.id === id);
    let next: number | null = null;
    if (event.key === "ArrowRight") next = (index + 1) % enabled.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + enabled.length) % enabled.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = enabled.length - 1;
    if (next === null) return;
    event.preventDefault();
    const target = enabled[next];
    if (!target) return;
    select(target.id);
    document.getElementById(`${base}-tab-${target.id}`)?.focus();
  };

  const current = tabs.find((t) => t.id === selected);
  return (
    <div className={clsx("tabs", className)}>
      <div role="tablist" aria-label={label} className="tabs__list">
        {tabs.map((tab) => {
          const isSelected = tab.id === selected;
          return (
            <button
              key={tab.id}
              id={`${base}-tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={isSelected}
              aria-controls={`${base}-panel-${tab.id}`}
              tabIndex={isSelected ? 0 : -1}
              disabled={tab.disabled}
              className="tabs__tab"
              onClick={() => select(tab.id)}
              onKeyDown={(event) => onKeyDown(event, tab.id)}
            >
              {tab.label}
              {tab.count !== undefined && <span className="tabs__count">{tab.count}</span>}
            </button>
          );
        })}
      </div>
      {current && (
        <div role="tabpanel" id={`${base}-panel-${current.id}`} aria-labelledby={`${base}-tab-${current.id}`} className="tabs__panel">
          {current.panel}
        </div>
      )}
    </div>
  );
}
